import { useState, useMemo } from "react";
import LmsFormGenerator, { BCFormController, FormActionsType, IFormGeneratorFieldOptions } from "../ui/LmsFormGenerator";
import { useParseRouteActions, useFetchData, getDropdownSemanticOptionsFromData, removeProp, appendParams, isEmptyValue } from "../../../helpers/GlobalHelper";
import LmsTable from "../ui/LmsTable";
import LmsTabPages from "../ui/LmsTabPages";
import { LmsParametersTable,LmsRolesTable } from "../ui/LmsSharedTables";
import { GlobalConstants } from "../../constants/GlobalConstants";

const LmsReportsTable = () => {
  const actionsButtons = ["export"]; // TODO: "add", "edit", "remove" descoped by Salam wi 14244
  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix="report/all"
    columnClickOptions={{
      reportTypeOption: { path: "reports-lms-reports/generate-report", idProp: "id" }
    }}
  />;
};

const LmsRolesSubTable = () => {
  const actionsButtons = [{text: "Add Selected", icon : GlobalConstants.Icons.SAVE,  isDisabled: (checkedItems) => checkedItems?.length !== 1 }, "close"];
  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix=""
  />;
};

const LmsReportsTabPages = ({ id }) => {
  const tabPages = {
    Parameters: <LmsParametersTable id={id} />,
    Roles: <LmsRolesTable id={id} />
  };

  return <LmsTabPages tabPages={tabPages}/>;
};

const addLmsReportFormFields = (myForm) => {
  myForm.addCenteredGroup([ 
    myForm.getInputField("Name", { name: "name", required: true }),
    myForm.getInputField("Report Action", { name: "reportAction", required: true }),
  ]);

  myForm.addCenteredGroup([ 
    myForm.getCheckboxField("Is Active", { value: true, inline: true, width: 3  }),
    myForm.getLabelField("Report Type", { required: true }),
    myForm.getGroupRadioField("Excel"),
    myForm.getGroupRadioField("Pdf"),
  ], { radioGroupName: "reportType", width: 3 });

  myForm.addCenteredGroup([
    myForm.getFileField("Document", { value: true }),
  ]);

  myForm.addCenteredGroup([
    myForm.getTextareaField("Description", { name: "description" }),
  ]);

  return myForm;
};

const addLmsParametersFormFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("LMS Report", "report/all", { name: "lmsReport", required: true, disabled: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Column Name", { required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Parameter Name", { required: true })]);
  return myForm;
};

const AddLmsReportForm = () => {
  const myForm = addLmsReportFormFields(new BCFormController({}, { submitSuffix: "" }));//TDO: Add Submit
  return (<LmsFormGenerator myForm={myForm} />);
};

const AddLmsParametersFormFieldsForm = ({ reportId }) => {
  const myForm = addLmsParametersFormFields(new BCFormController({ lmsReport: reportId }, { submitSuffix: "" }));//TDO: Add Submit
  return (<LmsFormGenerator myForm={myForm} />);
};

const EditLmsParametersFormFieldsForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`${id}`);
  const myForm = addLmsParametersFormFields(new BCFormController(data, { submitSuffix: "" }));
  const formActions = ["save", "save-close", "delete", "close"];
  return (<LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} />);
};

const EditLmsReportForm = ({ id }) => {
  const [{ data, isLoading, error }] = useFetchData(id ? `report/${id}` : "");
  const myForm = addLmsReportFormFields(new BCFormController(data, { submitSuffix: "" }));
  const formActions = ["save", "save-close", "delete", "close"];
  return (<LmsFormGenerator myForm={myForm} actions={formActions} isLoading={isLoading} error={error} />);
};

const GenerateLmsReportForm = ({ id }) => {
  const [showSuffix, setShowSuffix] = useState("");
  const [{ data, isLoading }] = useFetchData(id ? `report/${id}/parameters` : "");
  const myForm = new BCFormController(removeProp(data), { blankWidth: 6 });

  const conditionalFields = useMemo(() => {
    const conditionalFields = {} as any;
    data?.lmsReportConfigs?.map((config) => {
      if (config.conditionalOn) {
        conditionalFields[config.internalName] = { conditionalOn: config.conditionalOn, conditionalValue: config.conditionalValue };
      }
    });

    return conditionalFields;
  }, [data?.lmsReportConfigs]);

  const excludedFromFilters = useMemo(() => {
    const excludedParams = data?.lmsReportConfigs?.filter((config) => config.excludeFromFilter);
    if (excludedParams) {
      return excludedParams.map((config) => config.internalName);
    }

    return [];
  }, [data]);

  const fieldTypesDictionary = {
    DateYearMonth: GlobalConstants.FormInputTypes.YEAR_MONTH,
    Date: GlobalConstants.FormInputTypes.CALENDAR,
    Boolean: GlobalConstants.FormInputTypes.CHECKBOX
  };

  const getRadioFields = (config, myForm) => {
    const radioFields = [] as any[];
    radioFields.push(myForm.getLabelField(config.name, { required: config.isRequired }));
    const options = config.optionsJson ? JSON.parse(config.optionsJson) : [];
    options.forEach((option) => {
      radioFields.push(myForm.getGroupRadioField(option));
    });

    return radioFields;
  };

  if (data?.lmsReportConfigs) {
    data.lmsReportConfigs.forEach((config) => {
      const fieldType = fieldTypesDictionary[config.fieldType] ? fieldTypesDictionary[config.fieldType] : config.fieldType;
      const commonOptions = {
        name: config.internalName,
        required: config.isRequired,
        isHiddenFunc: ({ inputs }) => config.conditionalOn && inputs[config.conditionalOn] !== config.conditionalValue,
        fieldType: fieldTypesDictionary[config.fieldType] ? fieldTypesDictionary[config.fieldType] : config.fieldType,
        value: config.value
      } as IFormGeneratorFieldOptions;

      switch (fieldType) {
        case "dropdown":
          myForm.addCenteredGroup([
            myForm.getDropdownField(config.name, config.options ? getDropdownSemanticOptionsFromData(config.options) : config.optionsEndPoint, {
              ...commonOptions,
              dropdownMultiple: config.multiChoice,
            })
          ]);
          break;
        case "radio":
          myForm.addCenteredGroup(getRadioFields(config, myForm), { radioGroupName: config.internalName, radioGroupValue: config.value });
          break;
        case "calendar":
          myForm.addCenteredGroup([
            myForm.getCalendarField(config.name, commonOptions)
          ]);
          break;
        case "checkbox":
          myForm.addCenteredGroup([
            myForm.getCheckboxField(config.name, { ...commonOptions, alignRight: true })
          ]);
          break;
        default:
          myForm.addCenteredGroup([
            myForm.getInputField(config.name, { ...commonOptions })
          ]);
          break;
      }
    });
  }

  const IconsDictionary = {
    Excel: GlobalConstants.Icons.EXCEL_OUTLINE,
    Pdf: GlobalConstants.Icons.PDF_OUTLINE
  };

  const isFieldHidden = (inputs, internalName) => {
    if (conditionalFields[internalName]) {
      return inputs[conditionalFields[internalName].conditionalOn] !== conditionalFields[internalName].conditionalValue;
    }
  };

  const getEndPoint = (action, inputs) => {
    const params = [action !== "Show" ? { reportType: action } : {}] as any;
    Object.entries(inputs).forEach(([key, value] : [string, any]) => {
      if (!isEmptyValue(value) && !excludedFromFilters.includes(key) && !isFieldHidden(inputs, key)) {
        params.push({ [key]: value });
      }
    });

    return appendParams(data?.endPoint?.replace("{id}", id), params);
  };

  const getOptionsPath = (action) => {
    return {
      text: action,
      path: (inputs) => getEndPoint(action, inputs),
      action: GlobalConstants.FormDefaultAction.DOWNLOAD,
      icon: IconsDictionary[action]
    };
  };

  const formActions = [] as FormActionsType;
  if (data?.isShow) {
    formActions.push({
      text: "Show",
      action: GlobalConstants.FormDefaultAction.SAVE,
      actionHandleSubmit: (inputs) => setShowSuffix(getEndPoint("Show", inputs)),
      icon: GlobalConstants.Icons.SEARCH,
    });
  }

  if (data?.isExcel) {
    formActions.push(getOptionsPath("Excel"));
  }

  if (data?.isPdf) {
    formActions.push(getOptionsPath("Pdf"));
  }

  return <>
    <LmsFormGenerator myForm={myForm} actions={formActions} isLoading={isLoading} />
    {data?.isGrid && <LmsTable actionButtons={["export"]} apiSuffix={data?.endPoint?.replace("{id}", id)} />}
    {data?.isShow && showSuffix && <LmsTable actionButtons={["export"]} apiSuffix={showSuffix} />}
  </>;
};

const LmsReports = ({ params = {}, history }) => {
  const allowedActions = ["add"];
  const propertySubpages = ["addparameters", "editparameters", "addroles", "generate-report"];
  const allowedSubpages = [...propertySubpages] as string[];
  const { isAdd, isEdit, isMainPage, isActionAllowed, id, isSubpageAllowed, actionLower, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const reportId = query?.get("reportId");
  if (!isActionAllowed) {
      history.goBack();
  }

  return <>
    {isMainPage && <LmsReportsTable />}
    {isAdd && <AddLmsReportForm />}
    {isEdit && <>
       <EditLmsReportForm id={id} />
       <LmsReportsTabPages id={id} />
    </>}
    {isSubpageAllowed && <>
      {actionLower === propertySubpages[0] && <AddLmsParametersFormFieldsForm reportId={reportId} />}
      {actionLower === propertySubpages[1] && <EditLmsParametersFormFieldsForm id={id} />}
      {actionLower === propertySubpages[2] && <LmsRolesSubTable />}
      {actionLower === "generate-report" && <GenerateLmsReportForm id={id}/>}
    </>}
  </>;
};

export default LmsReports;